/**
 * Module dependencies.
 */

import { Text } from 'src/components/core/text';
import { media } from '@untile/react-core/styles/media';
import { motion } from 'framer-motion';
import { transparentize } from 'src/styles/utils/colors';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import styled from 'styled-components';

/*
 * `Props` type.
 */

type Props = {
  activeTab?: 'left' | 'right';
  handleActiveTab?: () => void;
  id: string;
  labelLeft: string;
  labelRight: string;
};

/*
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${transparentize('cyan800', 0.6)};
  border-radius: var(--border-radius-xl);
  display: flex;
  flex-wrap: nowrap;
  gap: 2px;
  padding: 4px;
  width: fit-content;
`;

/*
 * `TabWrapper` styled component.
 */

const TabWrapper = styled.button`
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--color-text);
  cursor: pointer;
  display: flex;
  height: var(--space-ms);
  outline: none;
  padding: 5px 12px;
  position: relative;
  transition: var(--transition-default);
  transition-property: color, background-color, height, padding;

  :focus,
  :focus-visible,
  :hover {
    background-color: ${transparentize('cyan800', 0.6)};
    color: var(--color-text);
  }

  &[data-active='true'] {
    color: var(--color-text-secondary);

    :focus,
    :focus-visible,
    :hover {
      color: var(--color-text-secondary);
    }
  }

  ${media.min.ms`
    height: 26px;
    padding: 3px 12px;
  `}
`;

/*
 * `TabBackground` styled component.
 */

const TabBackground = styled(motion.div)`
  background-color: var(--color-primary);
  border-radius: var(--border-radius-sm);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

/*
 * Export `Toggle` component.
 */

export function Toggle(props: Props) {
  const { activeTab = 'left', handleActiveTab, id, labelLeft, labelRight } = props;
  const isMobile = useBreakpoint(0, 'ms');

  const labelProps = {
    style: { position: 'relative' },
    variant: isMobile ? 'labelM' : 'labelS',
    weight: 600
  } as any;

  return (
    <Wrapper>
      <TabWrapper data-active={activeTab === 'left'} onClick={handleActiveTab}>
        {activeTab === 'left' && <TabBackground layoutId={id} />}

        <Text {...labelProps}>{labelLeft}</Text>
      </TabWrapper>
      <TabWrapper data-active={activeTab === 'right'} onClick={handleActiveTab}>
        {activeTab === 'right' && <TabBackground layoutId={id} />}

        <Text {...labelProps}>{labelRight}</Text>
      </TabWrapper>
    </Wrapper>
  );
}
